<template>
	<MyModal
		:value="isShow"
		title=""
		:max-width="maxWidth"
		persistent
		content-class="reprint-dialog"
	>
		<MyDeleteText
			:title="
				selectingPrinter
					? '所選單據未設定出單機，\n請選擇此次補印出單機。'
					: '選擇要補印的單據'
			"
		>
			<template v-if="selectingPrinter">
				<div>
					<div v-for="item in forceItems" :key="item.key" class="printer-row">
						<span class="receipt-label">{{ item.label }}</span>
						<div class="printer-choices">
							<v-checkbox
								v-for="printer in printers"
								:key="printer.machineType"
								v-model="printerSelections[item.key]"
								:label="printer.name"
								:value="printer.machineType"
								color="primary"
								multiple
								hide-details
								dense
							/>
						</div>
					</div>
				</div>
			</template>
			<template v-else>
				<div class="receipt-options">
					<div class="print-options-group">
						<v-checkbox
							v-for="item in basicItems"
							class="print-option-item"
							:key="item.key"
							v-model="printOptions[item.key]"
							:label="item.label"
							color="primary"
							hide-details
							dense
						/>
					</div>
					<div
						v-if="areaItems.length"
						class="print-options-group secondary-options"
					>
						<v-checkbox
							v-for="item in areaItems"
							class="print-option-item"
							:key="item.key"
							v-model="printOptions[item.key]"
							:label="item.label"
							color="primary"
							hide-details
							dense
						/>
					</div>
					<v-checkbox
						v-model="rememberSelection"
						label="記住此次選擇"
						color="primary"
						hide-details
						class="remember-selection"
						dense
					/>
				</div>
			</template>
		</MyDeleteText>
		<template #actions>
			<v-card-actions class="reprint-actions">
				<v-btn class="reprint-cancel" outlined large @click="cancel"
					>取消</v-btn
				>
				<v-btn
					large
					color="com1Color"
					:disabled="
						!checkReady || loading || (selectingPrinter && !allPrintersSelected)
					"
					@click="confirm"
					>列印</v-btn
				>
			</v-card-actions>
		</template>
	</MyModal>
</template>

<script>
import {
	createPrintOptions,
	restorePrintOptions,
	mergePrintOptions,
	hasAnyPrintSelection,
	getReprintPrinters,
	getForcePrintKeys,
	buildReprintOptions,
	PRINT_RECEIPT_TYPES,
} from '@/utils/printOptions'

export default {
	name: 'MyReprintModal',
	inject: {
		reprintQueryCoordinator: { default: null },
	},
	props: {
		maxWidth: {
			type: Number,
			default: 600,
		},
	},
	data() {
		return {
			isShow: false,
			loading: false,
			checkReady: false,
			submitting: false,
			requestVersion: 0,
			context: null,
			items: [],
			printers: [],
			zeroSettings: [],
			printOptions: createPrintOptions(),
			rememberSelection: true,
			selectingPrinter: false,
			forceKeys: [],
			printerSelections: {},
		}
	},
	computed: {
		visibleKeys() {
			return this.items.map(item => item.key)
		},
		basicItems() {
			return this.items.filter(item => !item.key.startsWith('printKitchenArea'))
		},
		areaItems() {
			return this.items.filter(item => item.key.startsWith('printKitchenArea'))
		},
		forceItems() {
			return this.items.filter(item => this.forceKeys.includes(item.key))
		},
		allPrintersSelected() {
			return this.forceKeys.every(key =>
				this.printers.some(printer =>
					this.printerSelections[key]?.includes(printer.machineType),
				),
			)
		},
	},
	beforeDestroy() {
		this.requestVersion++
	},
	methods: {
		/** 只讓待開窗查詢失效，不影響選機返回或已送出的 POST。 */
		cancelPendingQuery() {
			if (!this.loading) return
			this.requestVersion++
			this.loading = false
			this.checkReady = false
			this.isShow = false
		},
		/** 開窗即固定訂單、可見聯別與連線狀態，選機途中不改派。 */
		async show({
			orderId,
			deliveryOrder = false,
			includeTag = true,
			memoryKey = 'lastPrintOptions',
		}) {
			if (this.submitting) return
			const releaseQuery = this.reprintQueryCoordinator?.start(() =>
				this.cancelPendingQuery(),
			)
			const version = ++this.requestVersion
			const { storage, order } = this.$store.state
			this.context = {
				...this.$getStoreId(),
				orderId,
				deliveryOrder,
				memoryKey,
			}
			this.printers = getReprintPrinters(storage, order)
			const tagOnly =
				!this.printers.length && storage.hasTag && !!order.BTTagPrinter
			const items = []
			if (!tagOnly) {
				items.push(
					{ key: 'printStoreCopy', label: '店家聯' },
					{ key: 'printCustomerCopy', label: '客戶聯' },
				)
				if (!deliveryOrder && !storage.kitchenAreaSwitch)
					items.push({ key: 'printKitchenCopy', label: '廚房聯' })
			}
			if (includeTag) items.push({ key: 'printTag', label: '標籤貼紙' })
			if (!tagOnly && !deliveryOrder && storage.kitchenAreaSwitch) {
				for (const area of ['A', 'B', 'C'])
					items.push({
						key: `printKitchenArea${area}`,
						label: `廚房聯${area}區`,
					})
			}
			this.items = items
			this.printOptions = restorePrintOptions(
				storage[memoryKey],
				this.visibleKeys,
			)
			this.rememberSelection = true
			this.selectingPrinter = false
			this.forceKeys = []
			this.printerSelections = {}
			this.zeroSettings = []
			this.checkReady = false
			this.loading = true
			this.isShow = false
			try {
				const { storeId, orderId, deliveryOrder } = this.context
				const res = await this.$http.storeApiRouter.orderReport.printCheck({
					storeId,
					orderId,
					deliveryOrder,
				})
				if (version !== this.requestVersion) return
				if (!Array.isArray(res.data?.zeroSettings))
					throw new Error('無法取得補印設定，請重新開啟補印')
				this.zeroSettings = res.data.zeroSettings
				this.checkReady = true
				this.isShow = true
			} catch (error) {
				if (version === this.requestVersion) this.showError(error)
			} finally {
				releaseQuery?.()
				if (version === this.requestVersion) this.loading = false
			}
		},
		cancel() {
			if (this.submitting) return
			if (this.selectingPrinter) {
				this.selectingPrinter = false
				this.printerSelections = {}
				return
			}
			this.requestVersion++
			this.isShow = false
		},
		async confirm() {
			if (this.loading || !this.checkReady || this.submitting) return
			if (!hasAnyPrintSelection(this.printOptions)) {
				this.showError('請至少選擇一項單據')
				return
			}
			if (!this.selectingPrinter) {
				this.forceKeys = getForcePrintKeys(
					this.printOptions,
					this.visibleKeys,
					this.zeroSettings,
				)
				this.printerSelections = {}
				if (this.forceKeys.length) {
					if (!this.printers.length) {
						this.showError('請先連線出單機')
						return
					}
					if (this.printers.length > 1) {
						for (const key of this.forceKeys)
							this.$set(this.printerSelections, key, [])
						this.selectingPrinter = true
						return
					}
					for (const key of this.forceKeys)
						this.$set(this.printerSelections, key, [
							this.printers[0].machineType,
						])
				}
			} else if (!this.allPrintersSelected) return
			const targets = this.forceKeys.flatMap(key =>
				this.printerSelections[key].map(machineType => ({
					receiptType: PRINT_RECEIPT_TYPES[key],
					machineType,
				})),
			)
			const { memoryKey, ...params } = this.context
			const payload = {
				...params,
				...buildReprintOptions(this.printOptions, this.visibleKeys, targets),
			}
			this.submitting = true
			try {
				if (this.rememberSelection) {
					const mutation =
						memoryKey === 'lastReportPrintOptions'
							? 'setLastReportPrintOptions'
							: 'setLastPrintOptions'
					this.$store.commit(
						`storage/${mutation}`,
						mergePrintOptions(
							this.$store.state.storage[memoryKey],
							this.printOptions,
							this.visibleKeys,
						),
					)
				}
				this.isShow = false
				await this.$http.storeApiRouter.orderReport.printOrder(payload)
				this.$store.commit('showMessage', '已送出列印請求')
			} catch (error) {
				this.showError(error)
			} finally {
				this.submitting = false
			}
		},
		showError(error) {
			this.$store.commit('showMessage', {
				text: error?.message || error,
				color: 'error',
			})
		},
	},
}
</script>

<style lang="scss" scoped>
.receipt-options {
	width: fit-content;
	max-width: 100%;
	margin: 0 auto;
}
.print-options-group {
	display: flex;
	flex-wrap: wrap;
	gap: 4px 16px;
	margin-top: 8px;
	justify-content: flex-start;
}
.secondary-options {
	margin-top: 16px;
}
.print-option-item {
	flex: none;
}
.print-option-item ::v-deep .v-label {
	font-size: 20px;
}
.remember-selection {
	position: static;
	padding-top: 1rem;
	margin-top: 1rem;
}
.remember-selection::before {
	content: '';
	position: absolute;
	left: 0;
	right: 0;
	border-top: 1px solid #ddd;
	transform: translateY(-1rem);
}
.remember-selection ::v-deep .v-input__slot {
	justify-content: flex-start;
}
.remember-selection ::v-deep .v-label {
	flex: 0 1 auto;
}
.printer-row {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 1rem;
	text-align: left;
}
.receipt-label {
	flex: 0 0 auto;
}
.printer-choices {
	display: flex;
	flex-wrap: wrap;
	gap: 0 1.5rem;
	min-width: 0;
}
.printer-choices ::v-deep .v-label {
	font-size: 1.125rem;
	overflow-wrap: anywhere;
}
.printer-choices .v-input--checkbox {
	min-width: 0;
	max-width: 100%;
	margin-bottom: 0.5rem;
}
.reprint-actions {
	display: flex;
	gap: 0.5rem;
	padding: 1.5rem 0 0;
}
.reprint-actions .v-btn {
	flex: 1;
	height: auto;
	min-height: 2.75rem;
	padding: 0.75rem;
	font-size: 1rem;
}
.reprint-actions .reprint-cancel {
	color: $lightTextColor !important;
	border-color: $lineColor !important;
	border-width: 2px !important;
}
@media (max-width: 480px) {
	.printer-row {
		flex-direction: column;
		gap: 0;
	}
}
</style>
<style lang="scss">
.reprint-dialog.modal-dialog {
	overflow-y: auto;
	touch-action: pan-y;
}
.reprint-dialog.modal-dialog > .v-card .v-card__text {
	touch-action: pan-y;
}
.reprint-dialog .delete-text__title {
	white-space: pre-line;
}
.reprint-dialog .delete-text__txt {
	position: relative;
	width: 100%;
}
</style>
